// Fhir kit client
import Client from "fhir-kit-client";
// FHIR
import { Bundle, Parameters } from "fhir/r5";
// Translation
import i18n from "i18next";
// React
import { FunctionComponent, useCallback, useEffect, useState } from "react";
// React-bootstrap
import { Button, Form } from "react-bootstrap";
// React-router
import { useNavigate, useParams } from "react-router-dom";
// Components
import PandoraPage from "../../components/PandoraPage/PandoraPage";
import { useEnvironment } from "src/context/EnvironmentContext";

const ValidatorHome: FunctionComponent = () => {

  ////////////////////////////////////
  //            State               //
  ////////////////////////////////////

  const { currentEnvironment } = useEnvironment();
  const { validatorParam } = useParams();

  const [resource, setResource] = useState("");
  const [profiles, setProfiles] = useState([] as { name: string, url: string }[]);
  const [selectedProfile, setSelectedProfile] = useState("");
  const [file, setFile] = useState(null as File | null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  ////////////////////////////////////
  //            Client              //
  ////////////////////////////////////

  const fhirClient = new Client({
    baseUrl: currentEnvironment?.validateurUrl ?? process.env.REACT_APP_FHIR_URL ?? 'fhir'
  });

  ////////////////////////////////////
  //            LifeCycle           //
  ////////////////////////////////////

  /**
   * Load profiles when the page is loaded.
   * 
   */
  useEffect(() => {
    loadProfiles();
  }, []);

  ////////////////////////////////////
  //            Functions           //
  ////////////////////////////////////

  /**
    * Load Profiles from the back to populate Validation report page.
    *
    * @returns the promise of a Structure Definition.
    * 
    */
  async function loadProfiles() {
    setLoading(true);
    try {
      const response = await fhirClient.operation({
        name: '$profiles',
        method: 'GET',
        input: { "validatorId": validatorParam }
      })
      const results: Parameters = response as Parameters;
      const profiles: { name: string; url: string; }[] = [];
      results.parameter?.forEach(param => {
        profiles.push({ name: param.name, url: param.valueString ?? '' });
      })
      setProfiles(profiles);
      setLoading(false);
    } catch (error) {
      console.log(error);
      onError();
    }
  }

  /**
   * Redirect to the error page when an error occurs.
   * 
   */
  const onError = useCallback(() => {
    navigate("/Error");
  }, [navigate]);

  /**
    * Redirect to the report page.
    *
    * @param id ID of the Report to display.Added 'Main' component on TestReports page
    * 
    */
  const toValidationReport = useCallback((id: string) => {
    navigate("/ValidationReport/" + id);
  }, [navigate]);

  /**
    * Send validation to populate the Validation report page.
    * 
    */
  function sendToValidation() {
    setLoading(false);
    const requestHeaders: HeadersInit = new Headers();
    requestHeaders.set('Content-Type', 'application/fhir+json');
    buildValidationParametersAndSend();
    setLoading(true);
  }

  /**
    * Build validation parameters and send to populate the Validation report page.
    * 
    */
  function buildValidationParametersAndSend() {
    const parameters: Parameters = { resourceType: "Parameters" } as Parameters;
    parameters.parameter = [];
    parameters.parameter.push({ name: "validatorId", valueString: validatorParam ? validatorParam : "" });
    if (selectedProfile.length !== 0) {
      parameters.parameter.push({ name: "profile", valueCanonical: selectedProfile })
    }
    if (resource.length !== 0) {
      parameters.parameter.push({ name: "resource", valueString: resource })
      callValidation(parameters)
    } else if (file !== null) {
      const reader = new FileReader()
      reader.onload = async (e) => {
        const text = (e.target?.result);
        parameters?.parameter?.push({ name: "resource", valueString: text ? text as string : "" });
        callValidation(parameters);
      };
      reader.readAsText(file);
    }
  }

  /**
   * Call validation from the back to populate the Validation report page.
   *
   * @param parameters    the parameters to the Structure Definition.
   * 
   */
  function callValidation(parameters: Parameters) {
    fhirClient.operation({
      name: "validate",
      method: 'POST',
      input: JSON.stringify(parameters)
    })
      .then(async report => {
        const reportId = await resolveValidationReportId(report);
        if (!reportId) {
          onError();
          return;
        }
        toValidationReport(reportId);
      })
      .catch(error => {
        console.error(error)
        onError();
      });
  }

  async function resolveValidationReportId(report: any): Promise<string | undefined> {
    if (report?.id) {
      console.log("Validation report id found directly in response");
      return report.id;
    }

    const identifierValue = report?.identifier?.value;
    const identifierSystem = report?.identifier?.system;

    if (!identifierValue) {
      console.warn("Validation response does not contain identifier.value");
      return undefined;
    }

    const identifierSearch = identifierSystem
      ? `${identifierSystem}|${identifierValue}`
      : identifierValue;

    console.log("Searching Bundle by identifier", { identifierSearch });

    const searchResponse = await fhirClient.search({
      resourceType: "Bundle",
      searchParams: {
        identifier: identifierSearch,
        _count: "1"
      }
    });

    const bundleResult = searchResponse as Bundle;
    const bundleId = bundleResult.entry?.[0]?.resource?.id;

    if (!bundleId) {
      console.warn("No Bundle found for identifier", {
        identifierSearch,
        total: bundleResult.total
      });
    } else {
      console.log("Bundle id resolved from identifier search:", bundleId);
    }

    return bundleId;
  }

  /**
  * Get Profiles option to populate the select.
  *
  * @param name   the name to the Structure Definition.
  * @param url    the url to the Structure Definition.
  * 
  */
  function getProfileOptions(profile: { name: string, url: string }) {
    return <option key={profile.url} value={profile.url}>{profile.name + " (" + profile.url + ")"}</option>;
  }

  /**
   * Handle the change of the resource.
   * 
   * @param e 
   * 
   */
  function handleResourceChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = e.target.value;
    setResource(value);
    if (value.length === 0) {
      setFile(null);
    }
  }

  /**
   * Handle the change of the file.
   * 
   * @param e 
   * 
   */
  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const target = e.target as HTMLInputElement;
    const selectedFile = target.files ? target.files[0] : null;
    setFile(selectedFile || null);
    if (!selectedFile) {
      setResource('');
    }
  }

  /**
    * Reset the values of the resource, the resource of the file and the option of the select.
    * 
    */
  function onReset() {
    setLoading(true);
    setResource('');
    setFile(null);
    setSelectedProfile('');
    setLoading(false);
    const input = document.getElementById('inputFile') as HTMLInputElement;
    input.value = '';
  }

  ////////////////////////////////////
  //            Content             //
  ///////////////////////////////////

  return (
    <PandoraPage
      titleKey='title.validator'
      loading={loading}
      needsLogin={false}
    >
      <div className="d-flex justify-content-center align-items-center h-100">
        <div className="d-flex flex-column gap-4 w-75">
          <Form.Group>
            <Form.Label>
              {i18n.t('label.validator.fhirresource')} :
            </Form.Label>
            <Form.Control
              as="textarea"
              value={resource}
              rows={5}
              placeholder={i18n.t('placeholder.copyfhirresource').trimEnd()}
              type="text"
              onChange={handleResourceChange}
              disabled={file !== null && file !== undefined}
            />
          </Form.Group>
          <fieldset
            id="uploadInput"
          >
            <Form.Group>
              <Form.Label>
                {i18n.t('label.validator.uploadresource')} :
              </Form.Label>
              <Form.Control
                id="inputFile"
                type="file"
                onChange={handleFileChange}
                disabled={resource.length > 0}
              />
            </Form.Group>
          </fieldset>
          <Form.Group>
            <Form.Label>
              {i18n.t('label.validator.selectprofile')} :
            </Form.Label>
            <Form.Select
              value={selectedProfile}
              onChange={(e) => {
                setSelectedProfile(e.target.value);
              }}
            >
              <option>{i18n.t('label.validator.defaultprofileoption')}</option>
              {profiles.map(profile => getProfileOptions(profile))}
            </Form.Select>
          </Form.Group>
          <div className="d-flex align-items-center gap-4">
            <Button
              className="button"
              variant="primary"
              onClick={sendToValidation}
            >
              {i18n.t('button.validate')}
            </Button>
            <Button
              className="button"
              variant="secondary"
              onClick={onReset}
            >
              {i18n.t('button.reset')}
            </Button>
          </div>
        </div>
      </div>
    </PandoraPage>
  );
};

export default ValidatorHome;

